import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Loader2, ShoppingBag, Coins } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  WIRED_LOJA_GRUPOS,
  WiredLojaCatalogo,
  WiredLojaCatalogoItem,
  wiredService,
} from "../../services/wired";

const ZERO = BigInt(0);

function proximoValor(item: WiredLojaCatalogoItem): bigint {
  return item.modo === "incremento" ? BigInt(item.atual) + BigInt(item.valor) : BigInt(item.valor);
}

function proximoEfeito(e: { modo: string; atual: string; valor: string }): bigint {
  return e.modo === "incremento" ? BigInt(e.atual) + BigInt(e.valor) : BigInt(e.valor);
}

/** Motivo pelo qual o botão fica desabilitado; o servidor valida tudo de novo na compra. */
function motivoIndisponivel(item: WiredLojaCatalogoItem, pontos: bigint): string | null {
  if (item.restantes !== null && item.restantes <= 0) return "Limite atingido";
  if (item.requisitos.some((r) => !r.atendido)) return "Requisito não atendido";
  if (item.modo === "valor_fixo" && BigInt(item.atual) === BigInt(item.valor)) return "Já possui";
  if (item.modo === "incremento" && item.teto_valor !== null && proximoValor(item) > BigInt(item.teto_valor)) {
    return "Limite máximo";
  }
  const extraNoLimite = item.efeitos_extras.some(
    (e) => e.modo === "incremento" && e.teto_valor !== null && proximoEfeito(e) > BigInt(e.teto_valor)
  );
  if (extraNoLimite) return "Limite máximo";
  if (pontos < BigInt(item.custo)) return "Pontos insuficientes";
  return null;
}

export function LojaAtributos({ onComprou }: { onComprou?: () => void }) {
  const [catalogo, setCatalogo] = useState<WiredLojaCatalogo | null>(null);
  const [loading, setLoading] = useState(true);
  const [comprandoId, setComprandoId] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setCatalogo(await wiredService.obterLoja());
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar a loja", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const comprar = async (item: WiredLojaCatalogoItem) => {
    const novo = proximoValor(item);
    if (!window.confirm(`Comprar "${item.rotulo}" por ${item.custo} pontos? (${item.atual} → ${novo})`)) return;

    try {
      setComprandoId(item.id);
      const resposta = await wiredService.comprarLojaItem(item.id);
      enqueueSnackbar(resposta.mensagem, { variant: "success" });
      onComprou?.();
      // Atualiza com a resposta do servidor, sem nova leitura ao jogo.
      setCatalogo((atual) =>
        atual && atual.itens
          ? {
              ...atual,
              pontos: resposta.pontos,
              itens: atual.itens.map((i) =>
                i.id === item.id
                  ? {
                      ...i,
                      atual: resposta.valor,
                      efeitos_extras: i.efeitos_extras.map((e) => ({ ...e, atual: String(proximoEfeito(e)) })),
                      restantes: i.restantes === null ? null : Math.max(0, i.restantes - 1),
                    }
                  : i
              ),
            }
          : atual
      );
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Não foi possível concluir a compra", { variant: "error" });
      // Estoque, saldo ou valor podem ter mudado: recarrega para refletir o estado real.
      if (error.response?.status === 409) carregar();
    } finally {
      setComprandoId(null);
    }
  };

  if (loading && !catalogo) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pontos = catalogo?.pontos ? BigInt(catalogo.pontos) : ZERO;

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <ShoppingBag className="w-5 h-5" />
          Loja de Atributos
        </h2>
        <p className="text-sm text-muted-foreground">
          Troque seus pontos de atributo por melhorias de status e inventário
        </p>
      </div>

      {!catalogo?.disponivel && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">A loja não está disponível no momento.</CardContent>
        </Card>
      )}

      {catalogo?.disponivel && !catalogo.elegivel && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Você ainda não tem acesso à loja. Entre no jogo e tente novamente.
          </CardContent>
        </Card>
      )}

      {catalogo?.disponivel && catalogo.elegivel && (
        <>
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardContent className="py-4 flex items-center gap-3">
              <Coins className="w-6 h-6 text-[oklch(0.45_0.15_142)]" />
              <div>
                <p className="text-sm text-muted-foreground">Seus pontos de atributo</p>
                <p className="text-2xl font-bold text-foreground">{String(pontos)}</p>
              </div>
            </CardContent>
          </Card>

          {(catalogo.itens ?? []).length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">Nenhum item à venda.</CardContent>
            </Card>
          )}

          {WIRED_LOJA_GRUPOS.map((grupo) => {
            const itens = (catalogo.itens ?? []).filter((i) => i.grupo === grupo.value);
            if (itens.length === 0) return null;
            return (
              <Card key={grupo.value}>
                <CardHeader>
                  <CardTitle>{grupo.label}</CardTitle>
                  <CardDescription>Gaste pontos para melhorar seus atributos</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {itens.map((item) => {
                    const motivo = motivoIndisponivel(item, pontos);
                    return (
                      <div key={item.id} className="border border-border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-foreground">{item.rotulo}</h3>
                          <Badge variant="outline">{item.custo} pts</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          <span className="font-mono text-foreground">{item.atual}</span> →{" "}
                          <span className="font-mono text-foreground">{String(proximoValor(item))}</span>
                          {item.teto_valor !== null && item.modo === "incremento" ? ` (máx. ${item.teto_valor})` : ""}
                        </p>
                        {item.efeitos_extras.map((e) => (
                          <p key={e.rotulo} className="text-sm text-muted-foreground">
                            {e.rotulo}: <span className="font-mono text-foreground">{e.atual}</span> →{" "}
                            <span className="font-mono text-foreground">{String(proximoEfeito(e))}</span>
                          </p>
                        ))}
                        {item.requisitos.length > 0 && (
                          <div className="text-xs space-y-0.5">
                            <p className="text-muted-foreground">Requisitos:</p>
                            {item.requisitos.map((r) => (
                              <p
                                key={r.rotulo}
                                className={r.atendido ? "text-green-600" : "text-red-600"}
                              >
                                {r.atendido ? "✓" : "✗"} {r.tipo === "nivel" ? `Nível de ${r.rotulo}` : r.rotulo} ({r.operador} {r.valor}, você {r.tipo === "nivel" ? "está no nível" : "tem"} {r.atual})
                              </p>
                            ))}
                          </div>
                        )}
                        {item.restantes !== null && (
                          <p className="text-xs text-muted-foreground">
                            Você pode comprar mais {item.restantes} (limite de {item.limite_compras})
                          </p>
                        )}
                        <Button
                          className="w-full bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                          disabled={!!motivo || comprandoId !== null}
                          onClick={() => comprar(item)}
                        >
                          {comprandoId === item.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            motivo ?? "Comprar"
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}
